/**
 * Onboarding local state · slot_onboarding_redesign OB.3 (23 MAY 2026).
 *
 * Replaces the retired Sprint 0 spotlight tour (`tour-steps.ts` SPRINT_0_TOUR).
 * The new onboarding ("Первые шаги") has three beats — Studio Map card,
 * First-Steps checklist, three contextual hints — orchestrated by Onboarding.tsx.
 *
 * **Why a separate localStorage key, not `progress.onboarding`:**
 * `progress.onboarding` (OnboardingFlags) lives in ProgressContext / shared
 * schema — outside this slot's lock-zone, and `slot_supabase_migration` is
 * actively rewiring that surface. To avoid a two-chat collision the new
 * onboarding keeps its own key. The checklist's *progress* (open ticket,
 * check AC, quick-check, close ticket) is NOT stored here — it is derived
 * read-only from `progress` by FirstSteps.tsx. Only dismiss flags + the one
 * non-derivable step (glossary) + hint-seen flags persist here.
 *
 * Cross-device note: this key is per-device (not synced via the Progress
 * JSONB). Acceptable for a first-session onboarding — worst case a second
 * device shows the checklist again, and the explicit "Скрыть" dismisses it.
 * If sync is later wanted, fold into progress.onboarding (needs ProgressContext
 * coordination — see slot log [note]).
 */

import { useCallback, useEffect } from 'react';
import { useLocalStorage } from '@/hooks/useLocalStorage';

export const ONBOARDING_STORAGE_KEY = 'qaStudio_onboarding_v1';

/** Window event dispatched by restartOnboarding() — re-arms the onboarding same-tab. */
export const ONBOARDING_RESTART_EVENT = 'qa-onboarding-restart';

export type OnboardingHintKey =
  | 'nav'
  | 'glossaryTerm'
  | 'pomodoro'
  | 'board'
  | 'map'
  | 'progress'
  | 'profile';

export interface OnboardingLocalState {
  /** «я уже всё это знаю — пропустить» on Welcome — suppresses the whole tutorial. */
  skipped: boolean;
  /** «Карта студии» card has been dismissed. */
  studioMapDismissed: boolean;
  /** «Первые шаги» checklist has been hidden (explicit «Скрыть» or 5/5 done). */
  firstStepsHidden: boolean;
  /** Checklist step 5 («Загляни в Глоссарий») — the one non-derivable step. */
  glossaryStepDone: boolean;
  /** Contextual hints that have already been shown + dismissed. */
  hintsSeen: Record<OnboardingHintKey, boolean>;
}

export const DEFAULT_ONBOARDING_STATE: OnboardingLocalState = {
  skipped: false,
  studioMapDismissed: false,
  firstStepsHidden: false,
  glossaryStepDone: false,
  hintsSeen: {
    nav: false,
    glossaryTerm: false,
    pomodoro: false,
    board: false,
    map: false,
    progress: false,
    profile: false,
  },
};

/** Coerce any stored / partial value into a complete, well-typed state. */
function normalize(raw: Partial<OnboardingLocalState> | null | undefined): OnboardingLocalState {
  return {
    skipped: !!raw?.skipped,
    studioMapDismissed: !!raw?.studioMapDismissed,
    firstStepsHidden: !!raw?.firstStepsHidden,
    glossaryStepDone: !!raw?.glossaryStepDone,
    hintsSeen: {
      nav: !!raw?.hintsSeen?.nav,
      glossaryTerm: !!raw?.hintsSeen?.glossaryTerm,
      pomodoro: !!raw?.hintsSeen?.pomodoro,
      board: !!raw?.hintsSeen?.board,
      map: !!raw?.hintsSeen?.map,
      progress: !!raw?.hintsSeen?.progress,
      profile: !!raw?.hintsSeen?.profile,
    },
  };
}

export interface UseOnboardingStateResult {
  state: OnboardingLocalState;
  dismissStudioMap: () => void;
  hideFirstSteps: () => void;
  markGlossaryStep: () => void;
  markHintSeen: (key: OnboardingHintKey) => void;
}

/**
 * Onboarding-local state hook. Persists to `ONBOARDING_STORAGE_KEY`.
 * Listens for the restart event so the "Перепройти знакомство" entry point
 * re-arms the onboarding without a full page reload.
 */
export function useOnboardingState(): UseOnboardingStateResult {
  const [stored, setStored] = useLocalStorage<OnboardingLocalState>(
    ONBOARDING_STORAGE_KEY,
    DEFAULT_ONBOARDING_STATE,
  );
  const state = normalize(stored);

  useEffect(() => {
    // Re-read the stored value (restartOnboarding / skipOnboarding write it
    // module-level, outside React) so a mounted Onboarding syncs same-tab.
    const onSync = () => {
      try {
        const raw = window.localStorage.getItem(ONBOARDING_STORAGE_KEY);
        setStored(raw ? (JSON.parse(raw) as OnboardingLocalState) : DEFAULT_ONBOARDING_STATE);
      } catch {
        setStored(DEFAULT_ONBOARDING_STATE);
      }
    };
    window.addEventListener(ONBOARDING_RESTART_EVENT, onSync);
    return () => window.removeEventListener(ONBOARDING_RESTART_EVENT, onSync);
  }, [setStored]);

  const dismissStudioMap = useCallback(
    () => setStored((s) => ({ ...normalize(s), studioMapDismissed: true })),
    [setStored],
  );
  const hideFirstSteps = useCallback(
    () => setStored((s) => ({ ...normalize(s), firstStepsHidden: true })),
    [setStored],
  );
  const markGlossaryStep = useCallback(
    () => setStored((s) => ({ ...normalize(s), glossaryStepDone: true })),
    [setStored],
  );
  const markHintSeen = useCallback(
    (key: OnboardingHintKey) =>
      setStored((s) => {
        const n = normalize(s);
        return { ...n, hintsSeen: { ...n.hintsSeen, [key]: true } };
      }),
    [setStored],
  );

  return { state, dismissStudioMap, hideFirstSteps, markGlossaryStep, markHintSeen };
}

/**
 * Reset the onboarding — re-arms Studio Map, First-Steps checklist and all
 * hints. Wired to the "Перепройти знакомство" settings entry point. Writes the
 * default state and dispatches ONBOARDING_RESTART_EVENT so a mounted
 * Onboarding picks it up in the same tab (no reload needed).
 */
/**
 * Suppress the tutorial entirely — Welcome «я уже всё это знаю — пропустить».
 * Writes a fresh state with `skipped: true` (also clearing any stale dismiss
 * flags left in this browser by previous accounts) and dispatches the sync
 * event for a mounted Onboarding.
 */
export function skipOnboarding(): void {
  try {
    window.localStorage.setItem(
      ONBOARDING_STORAGE_KEY,
      JSON.stringify({ ...DEFAULT_ONBOARDING_STATE, skipped: true }),
    );
  } catch {
    /* localStorage unavailable — event dispatch still syncs live state */
  }
  window.dispatchEvent(new Event(ONBOARDING_RESTART_EVENT));
}

export function restartOnboarding(): void {
  try {
    window.localStorage.setItem(ONBOARDING_STORAGE_KEY, JSON.stringify(DEFAULT_ONBOARDING_STATE));
  } catch {
    /* localStorage unavailable (private mode) — event dispatch still re-arms live state */
  }
  window.dispatchEvent(new Event(ONBOARDING_RESTART_EVENT));
}
